/**
 * @file This file contains the main application component for the project.
 */
import React, {useEffect, useMemo, useState} from 'react';
import {Col, Container, Row} from 'react-bootstrap';
import Sidebar from './components/Sidebar';
import Calendar from './components/Calendar';
import {ThemeProvider, useTheme} from './components/ThemeContext';
import './App.css';
import {DEPARTMENTS, Department, IProductionZone, ITraveler} from './data/interfaces';
import {Navigate, Route, Routes, useParams} from 'react-router-dom';
import {fetchUnscheduledTravelers} from './data/fetchTravelers';
import {hideTravelerFromAssemblyTaskList} from './data/hideTravelerFromAssemblyTaskList';
import {subscribeToProductionZones} from './data/productionZones';
import {loadSelectedProductionZoneIdPreference, loadSidebarCollapsedPreference, saveSelectedProductionZoneIdPreference, saveSidebarCollapsedPreference} from './utils/calendarUiPreferences';
import {DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT} from './utils/departmentUtils';

/**
 * MainContent is a functional component that renders the main content of the application.
 * It includes a sidebar and a header, and manages the state for whether the sidebar is collapsed.
 * @returns {JSX.Element} The rendered component.
 */
const MainContent: React.FC = () => {

  const {department, date} = useParams<{ department: string; date: string }>();
  const [currentDepartment, setCurrentDepartment] = useState<Department>(DEPARTMENTS.installation);
  const [listenerUnscheduledTravelers, setListenerUnscheduledTravelers] = useState<ITraveler[]>([]);
  const [optimisticallyHiddenTravelerIds, setOptimisticallyHiddenTravelerIds] = useState<Set<string>>(() => new Set());
  const [selectedTravelerId, setSelectedTravelerId] = useState<string | undefined>(undefined);
  const [productionZones, setProductionZones] = useState<IProductionZone[]>([]);
  const [selectedProductionZoneId, setSelectedProductionZoneId] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState(
    () => loadSidebarCollapsedPreference() ?? false
  );
  const {theme} = useTheme();
  const unscheduledTravelers = listenerUnscheduledTravelers.filter(
    (traveler) => !optimisticallyHiddenTravelerIds.has(traveler.id)
  );
  const selectedProductionZone = useMemo(
    () => productionZones.find((productionZone) => productionZone.id === selectedProductionZoneId) ?? null,
    [productionZones, selectedProductionZoneId]
  );
  const sortedProductionZones = useMemo(
    () => [...productionZones].sort((a, b) => a.name.localeCompare(b.name)),
    [productionZones]
  );

  /**
   * Toggles the state of the sidebar (collapsed/expanded).
   */
  const toggleSidebar = () => {
    setIsCollapsed((currentIsCollapsed) => {
      const nextIsCollapsed = !currentIsCollapsed;
      saveSidebarCollapsedPreference(nextIsCollapsed);
      return nextIsCollapsed;
    });
  };

  const handleProductionZoneChange = (productionZoneId: string) => {
    saveSelectedProductionZoneIdPreference(productionZoneId);
    setSelectedProductionZoneId(productionZoneId);
  };

  const resolveCalendarToDepartment = (calendar?: string): Department => {
    switch (calendar) {
      case DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT[DEPARTMENTS.customerService]:
        return DEPARTMENTS.customerService;
      case DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT[DEPARTMENTS.assembly]:
        return DEPARTMENTS.assembly;
      case DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT[DEPARTMENTS.production]:
        return DEPARTMENTS.production;
      default:
        return DEPARTMENTS.installation;
    }
  };

  useEffect(() => {
    document.body.className = theme;
  }, [theme]);

  useEffect(() => {
    setCurrentDepartment(resolveCalendarToDepartment(department));
  }, [department]);

  useEffect(() => {
    if (currentDepartment !== DEPARTMENTS.production) {
      setProductionZones([]);
      return;
    }

    return subscribeToProductionZones(
      setProductionZones,
      (error) => {
        console.error(`Error fetching ${DEPARTMENTS.production} zones:`, error);
        setProductionZones([]);
      }
    );
  }, [currentDepartment]);

  useEffect(() => {
    if (currentDepartment !== DEPARTMENTS.production || sortedProductionZones.length === 0) {
      return;
    }

    const hasSelectedZone = sortedProductionZones.some(
      (productionZone) => productionZone.id === selectedProductionZoneId
    );
    if (hasSelectedZone) {
      return;
    }

    const savedProductionZoneId = loadSelectedProductionZoneIdPreference();
    const savedProductionZone = sortedProductionZones.find(
      (productionZone) => productionZone.id === savedProductionZoneId
    );
    const defaultProductionZone =
      savedProductionZone
      ?? sortedProductionZones.find((productionZone) => productionZone.name === 'B4 Fab')
      ?? sortedProductionZones[0];
    setSelectedProductionZoneId(defaultProductionZone.id);
  }, [currentDepartment, selectedProductionZoneId, sortedProductionZones]);

  // Keep optimistic hidden IDs in sync with the latest traveler listener snapshot.
  useEffect(() => {
    const visibleTravelerIds = new Set(listenerUnscheduledTravelers.map((traveler) => traveler.id));
    setOptimisticallyHiddenTravelerIds((currentHiddenTravelerIds) => {
      let hasChanges = false;
      const nextHiddenTravelerIds = new Set<string>();

      currentHiddenTravelerIds.forEach((travelerId) => {
        if (visibleTravelerIds.has(travelerId)) {
          nextHiddenTravelerIds.add(travelerId);
          return;
        }
        hasChanges = true;
      });

      return hasChanges ? nextHiddenTravelerIds : currentHiddenTravelerIds;
    });
  }, [listenerUnscheduledTravelers]);

  // Fetch unscheduled travelers when the department changes
  useEffect(() => {
    const handleDataChange = (unscheduledTravelersData: ITraveler[]) => {

      const filteredTravelers = unscheduledTravelersData.filter((traveler) => {
        const isValid = traveler && traveler.workOrderType !== 'In-House Work';
        return isValid;
      });

      setListenerUnscheduledTravelers(filteredTravelers);
    };

    const handleError = (error: Error) => {
      console.error('Error fetching unscheduled travelers:', error);
    };

    const unsubscribe = fetchUnscheduledTravelers(
      currentDepartment,
      handleDataChange,
      handleError,
      selectedProductionZone
    );

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentDepartment, selectedProductionZone]);

  const handleTravelerClick = (travelerId: string) => {
    setSelectedTravelerId(travelerId);
  };

  const handleHideTraveler = async (traveler: ITraveler) => {
    setOptimisticallyHiddenTravelerIds((currentHiddenTravelerIds) => {
      const nextHiddenTravelerIds = new Set(currentHiddenTravelerIds);
      nextHiddenTravelerIds.add(traveler.id);
      return nextHiddenTravelerIds;
    });

    try {
      await hideTravelerFromAssemblyTaskList(traveler.id);
    } catch (error) {
      setOptimisticallyHiddenTravelerIds((currentHiddenTravelerIds) => {
        if (!currentHiddenTravelerIds.has(traveler.id)) {
          return currentHiddenTravelerIds;
        }

        const nextHiddenTravelerIds = new Set(currentHiddenTravelerIds);
        nextHiddenTravelerIds.delete(traveler.id);
        return nextHiddenTravelerIds;
      });
      alert('Failed to hide traveler. Please try again.');
    }
  };

  return (
    <Row className="m-0 p-0 vh-100 min-vh-100 overflow-hidden">
      {/* Sidebar */}
      <Col
        xs="auto"
        md={isCollapsed ? 'auto' : 2}
        className={`p-0 d-none d-md-block overflow-hidden h-100 ${isCollapsed ? 'collapsed' : ''}`}
      >
        <Sidebar
          isCollapsed={isCollapsed}
          department={currentDepartment}
          unscheduledTravelers={unscheduledTravelers}
          onTravelerClick={handleTravelerClick}
          onHideTraveler={handleHideTraveler}
        />
      </Col>
      {/* Main Content */}
      <Col xs={isCollapsed ? 12 : 10} className="p-0 h-100 overflow-hidden">
        <div className={`content h-100 ${isCollapsed ? 'expanded' : ''} ps-3 `}>
          <Container fluid className="p-0 h-100">
            <Calendar
              isCollapsed={isCollapsed}
              toggleSidebar={toggleSidebar}
              currentDepartment={currentDepartment}
              initialDate={date}
              unscheduledTravelersCount={unscheduledTravelers.length}
              selectedTravelerId={selectedTravelerId}
              setSelectedTravelerId={setSelectedTravelerId}
              productionZones={sortedProductionZones}
              selectedProductionZone={selectedProductionZone}
              setSelectedProductionZoneId={handleProductionZoneChange}
            />
          </Container>
        </div>
      </Col>
    </Row>
  );
};

/**
 * App is the root component of the application.
 * It provides the ThemeContext to its children and renders the MainContent component.
 * @returns {JSX.Element} The rendered component.
 */
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <Routes>
        <Route path="/:department/:date" element={<MainContent/>}/>
        <Route path="/:department" element={<MainContent/>}/>
        <Route path="/" element={<Navigate to={`/${DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT[DEPARTMENTS.installation]}/`} replace/>}/>
      </Routes>
    </ThemeProvider>
  );
};

export default App;
